import {
  buildReleasePack,
  computeWeightBalance,
  convertFuel,
  formatDutyHours,
  formatFuelFigure,
  fuelKindForType,
  hydrateDutyRoster,
  hydrateWeightBalanceProfile,
  isFuelUnit,
  ofpMassUnit,
  overlayLiveFleetLimits,
  resolveAircraftFuelUnit,
  tripSupportOf,
  dutyViewFor,
  formatDepartureLead,
  hydrateHeroStrip,
  publishHeroStrip,
  type AircraftSpec,
  type DutyRoster,
  type FlightLeg,
  type FuelKind,
  type FuelUnit,
  type MelItem,
  type Mission,
  type NotamBrief,
  type PilotLogbookEntry,
  type Squawk,
  type WeightBalanceDesk,
} from "@northstar/dispatch-core";
import { projectCrewSnapshot, type CompanionSnapshotSource } from "./crewSnapshot";
import type { EditTab } from "./shell/editTabs";

export type PilotLeg = {
  seq: number;
  route: string;
  out: string;
  fuel: string;
};

export type PilotCheck = {
  id: string;
  label: string;
  state: "done" | "missing" | "partial";
  detail: string;
};

export type PilotLogRow = {
  id: string;
  out: string;
  inn: string;
  fuel: string;
};

export type PilotNote = { id: string; text: string };

export type PilotScheduleRow = {
  id: string;
  callsign: string;
  route: string;
  tail: string;
  departureIso: string;
  label: string;
};

export type PilotHandover = { fob: string; note: string };

export type PilotSimTimes = {
  offBlock: string;
  takeoff: string;
  landing: string;
  onBlock: string;
};

export type PilotHero = {
  active: number;
  onGround: number;
  inAir: number;
  airborne: { registration: string; flight: string; route: string; eteLabel: string; etaLabel: string }[];
  cautionCount: number;
  emergencyCount: number;
  alerts: { id: string; tone: "info" | "warn" | "alert"; source: string; text: string }[];
  stdIso: string;
  stdLabel: string;
  flight: string;
  route: string;
  lead: string;
};

export type PilotBoard = {
  companyName: string;
  companyLogo: string;
  homeIcao: string;
  crewName: string;
  canDispatchWrite: boolean;
  tripCount: number;
  tripId: string | null;
  callsign: string;
  route: string;
  tail: string;
  aircraftName: string;
  std: string;
  sta: string;
  ete: string;
  statusLabel: string;
  statusClass: string;
  legs: PilotLeg[];
  fuel: string[];
  wb: string[];
  wbWithin: boolean | null;
  pax: string;
  paxNames: string[];
  catering: string[];
  notams: PilotNote[];
  notamNote: string;
  originMetar: string;
  destMetar: string;
  ofp: string[];
  ofpPdf: string;
  duty: {
    statusLabel: string;
    onDuty: boolean;
    level: "ok" | "warn" | "alert";
    fdp: string;
    fdpLimit: string;
    flight: string;
    flightLimit: string;
    rest: string;
    restLimit: string;
  } | null;
  handover: PilotHandover | null;
  simTimes: PilotSimTimes;
  /** Desk logbook rows for this flight. */
  logbook: PilotLogbookRow[];
  schedule: PilotScheduleRow[];
  checks: PilotCheck[];
  log: PilotLogRow[];
  squawks: PilotNote[];
  messages: PilotNote[];
  acarsHoppie: boolean;
  acarsUplinkIds: string[];
  crash: { title: string; body: string } | null;
  hero: PilotHero;
};

export type PilotLogbookRow = {
  id: string;
  flightTimeMin: number;
  airTimeMin: number;
  landingsDay: number;
  landingsNight: number;
  registration: string;
  aircraftType: string;
  route: string;
  role: string;
};

type LogRow = {
  id?: string;
  tripId?: string;
  registration?: string;
  departedAtUtc?: string;
  arrivedAtUtc?: string;
  offBlocksUtc?: string;
  onBlocksUtc?: string;
  wheelsOffUtc?: string;
  touchdownUtc?: string;
  arrival?: {
    fuelUsedAmount?: number;
    fuelUsedUnit?: string;
    notes?: string;
    maintenanceNotes?: string;
    squawkIds?: string[];
  } | null;
};

export type PilotPayload = CompanionSnapshotSource & {
  flightLogs?: readonly LogRow[] | null;
  squawks?: readonly Squawk[] | null;
  weightBalance?: WeightBalanceDesk | null;
  opsNotamBrief?: NotamBrief | null;
  melItems?: readonly MelItem[] | null;
  /** Present on the desk snapshot. The phone does not read it. */
  inbox?: unknown;
  logbook?: readonly PilotLogbookEntry[] | null;
};

const EMPTY_SIM: PilotSimTimes = { offBlock: "", takeoff: "", landing: "", onBlock: "" };

const EMPTY_HERO: PilotHero = {
  active: 0,
  onGround: 0,
  inAir: 0,
  airborne: [],
  cautionCount: 0,
  emergencyCount: 0,
  alerts: [],
  stdIso: "",
  stdLabel: "",
  flight: "",
  route: "",
  lead: "—",
};

function heroFromPayload(payload: PilotPayload, now: Date, dutyOverride?: DutyRoster | null): PilotHero {
  const published =
    hydrateHeroStrip(payload.heroStrip) ??
    publishHeroStrip({
      now,
      missions: payload.missions ?? [],
      fleet: payload.fleet ?? [],
      crew: payload.crew ?? [],
      duty: dutyOverride ?? payload.duty ?? undefined,
      assignments: payload.assignments ?? [],
      scenery: payload.scenery ?? [],
      company: payload.company
        ? { enforceDutyClock: payload.company.enforceDutyClock !== false, homeIcao: payload.company.homeIcao ?? "", mxBases: [] }
        : null,
      profileName: payload.profile?.name,
    });
  const stdMs = published.departure?.stdIso ? Date.parse(published.departure.stdIso) : Number.NaN;
  return {
    active: published.fleet.active,
    onGround: published.fleet.onGround,
    inAir: published.fleet.inAir,
    airborne: published.fleet.airborne,
    cautionCount: published.cautionCount,
    emergencyCount: published.emergencyCount,
    alerts: published.alerts.map((alert) => ({
      id: alert.id,
      tone: alert.tone,
      source: alert.source,
      text: alert.text,
    })),
    stdIso: published.departure?.stdIso ?? "",
    stdLabel: published.departure?.stdLabel ?? "",
    flight: published.departure?.flight ?? "",
    route: published.departure?.route ?? "",
    lead: Number.isFinite(stdMs) ? formatDepartureLead(stdMs - now.getTime()) : "—",
  };
}

const EMPTY: PilotBoard = {
  companyName: "",
  companyLogo: "",
  homeIcao: "",
  crewName: "",
  canDispatchWrite: false,
  tripCount: 0,
  tripId: null,
  callsign: "",
  route: "",
  tail: "",
  aircraftName: "",
  std: "STD —",
  sta: "STA —",
  ete: "—",
  statusLabel: "",
  statusClass: "",
  legs: [],
  fuel: [],
  wb: [],
  wbWithin: null,
  pax: "",
  paxNames: [],
  catering: [],
  notams: [],
  notamNote: "",
  originMetar: "",
  destMetar: "",
  ofp: [],
  ofpPdf: "",
  duty: null,
  handover: null,
  simTimes: { ...EMPTY_SIM },
  logbook: [],
  schedule: [],
  checks: [],
  log: [],
  squawks: [],
  messages: [],
  acarsHoppie: false,
  acarsUplinkIds: [],
  crash: null,
  hero: { ...EMPTY_HERO },
};

function zulu(iso: string | undefined): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "—";
  return `${new Date(t).toISOString().slice(11, 16)}Z`;
}

function fuelText(
  gal: number | undefined,
  lbs: number | undefined,
  unit: FuelUnit,
  kind: FuelKind,
): string {
  if (typeof lbs === "number" && Number.isFinite(lbs) && lbs > 0) {
    return formatFuelFigure(convertFuel(lbs, "lb", unit, kind), unit);
  }
  if (typeof gal === "number" && Number.isFinite(gal) && gal > 0) {
    return formatFuelFigure(convertFuel(gal, "gal", unit, kind), unit);
  }
  return "";
}

function legRow(leg: FlightLeg, seq: number, unit: FuelUnit, kind: FuelKind): PilotLeg {
  return {
    seq,
    route: `${leg.originIcao}–${leg.destIcao}`,
    out: zulu(leg.departureIso),
    fuel: fuelText(leg.fuelGal, leg.fuelLbs, unit, kind),
  };
}

function fuelLines(mission: Mission, unit: FuelUnit, kind: FuelKind): string[] {
  const lines: string[] = [];
  const plan = mission.ofpAttachment?.fuelPlan;
  if (plan) {
    const mass = ofpMassUnit(plan.units);
    const add = (label: string, value: number | undefined) => {
      if (typeof value !== "number" || !Number.isFinite(value)) return;
      const shown = mass ? convertFuel(value, mass, unit, kind) : value;
      lines.push(`${label} ${formatFuelFigure(shown, mass ? unit : "lb")}`);
    };
    add("Taxi", plan.taxi);
    add("Trip", plan.trip);
    add("Contingency", plan.contingency);
    add("Alternate", plan.alternate);
    add("Reserve", plan.reserve);
    add("Extra", plan.extra);
    add("Min takeoff", plan.minTakeoff);
    add("Plan takeoff", plan.planTakeoff);
    add("Plan ramp", plan.planRamp);
    add("Plan landing", plan.planLanding);
  }
  const ticket = fuelText(mission.fuelGal, mission.fuelLbs, unit, kind);
  if (ticket) lines.push(`Ticket ${ticket}`);
  return lines;
}

function wbLines(
  fleet: readonly AircraftSpec[] | null | undefined,
  desk: WeightBalanceDesk | null | undefined,
  mission: Mission,
): { lines: string[]; within: boolean | null } {
  const spec = (fleet ?? []).find((row) => row.id === mission.aircraftId);
  const saved = spec && desk?.profiles ? desk.profiles[spec.id] : undefined;
  if (!spec || !saved) return { lines: [], within: null };
  try {
    const profile = overlayLiveFleetLimits(hydrateWeightBalanceProfile(saved, spec), spec);
    const result = computeWeightBalance(profile);
    const lines = [
      `ZFW ${Math.round(result.zfw.weightLb)} lb`,
      `Ramp ${Math.round(result.ramp.weightLb)} lb`,
      `TOW ${Math.round(result.tow.weightLb)} lb`,
      `LDG ${Math.round(result.ldg.weightLb)} lb`,
    ];
    if (result.withinLimits) lines.push("Inside the saved envelope");
    else if (result.reasons[0]) lines.push(result.reasons[0]);
    return { lines, within: result.withinLimits };
  } catch {
    return { lines: ["W&B sheet could not be read."], within: null };
  }
}

function httpsPdf(url: string | undefined): string {
  if (!url) return "";
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" ? parsed.toString() : "";
  } catch {
    return "";
  }
}

function clockPair(elapsed: number | null | undefined, remain: number | null | undefined): string {
  if (elapsed == null && remain == null) return "—";
  const used = elapsed == null ? "—" : formatDutyHours(elapsed);
  const left = remain == null ? "—" : formatDutyHours(remain);
  return `${used} used · ${left} left`;
}

export function buildPilotBoard(
  payload: PilotPayload | null | undefined,
  crewId?: string | null,
  now = new Date(),
  dutyOverride?: DutyRoster | null,
  focusMissionId?: string | null,
): PilotBoard {
  if (!payload) return { ...EMPTY };
  const view = projectCrewSnapshot(payload, { crewId: crewId ?? undefined });
  const focusId = focusMissionId?.trim() ?? "";
  const focused = focusId ? view.trips.find((row) => row.id === focusId) ?? null : null;
  const trip = focused ?? view.nextTrip;
  const mission = (payload.missions ?? []).find((row) => row.id === trip?.id) ?? null;
  const crew = view.crewId ? { id: view.crewId, name: view.crewName || view.crewId } : null;
  const dutyView = crew
    ? dutyViewFor(dutyOverride ?? hydrateDutyRoster(payload.duty ?? undefined), crew, now)
    : null;

  const tail = mission
    ? (payload.fleet ?? []).find(
        (row) => row.id === mission.aircraftId || row.registration === mission.registration,
      )
    : undefined;
  const companyUnit = isFuelUnit(payload.company?.defaultFuelUnit) ? payload.company.defaultFuelUnit : undefined;
  const fuelUnit = resolveAircraftFuelUnit(tail, companyUnit);
  const fuelKind = fuelKindForType(tail?.icaoType || mission?.ofpAttachment?.aircraftType);
  const legs = mission
    ? mission.legs?.length
      ? mission.legs.map((leg, index) => legRow(leg, leg.seq || index + 1, fuelUnit, fuelKind))
      : [
          {
            seq: 1,
            route: trip?.route || `${mission.originIcao}–${mission.destIcao}`,
            out: zulu(mission.departureIso),
            fuel: fuelText(mission.fuelGal, mission.fuelLbs, fuelUnit, fuelKind),
          },
        ]
    : [];

  const support = mission ? tripSupportOf(mission) : [];
  const catering = support
    .filter((line) => line.serviceType === "catering")
    .map((line) => {
      const vendor = line.vendorName?.trim();
      const notes = line.notes?.trim();
      return [line.airportIcao, line.status, vendor, notes].filter(Boolean).join(" · ");
    })
    .filter(Boolean);

  const brief = payload.opsNotamBrief;
  const notams = (brief?.items ?? [])
    .filter((item) => item.text?.trim())
    .map((item) => ({ id: item.id || `${item.icao}-${item.text.slice(0, 24)}`, text: `${item.icao} · ${item.text.trim()}` }));

  const ofp = mission?.ofpAttachment;
  const ofpLines: string[] = [];
  if (ofp) {
    if (ofp.route?.trim()) ofpLines.push(ofp.route.trim());
    if (ofp.matchNote?.trim()) ofpLines.push(ofp.matchNote.trim());
    if (ofp.distanceNm?.trim()) ofpLines.push(`Distance ${ofp.distanceNm.trim()}`);
    if (ofp.retrievedAtUtc) ofpLines.push(`Retrieved ${zulu(ofp.retrievedAtUtc)}`);
    if (ofp.originIcao && ofp.destIcao) ofpLines.push(`${ofp.originIcao}–${ofp.destIcao}`);
  }

  const wb = mission ? wbLines(payload.fleet, payload.weightBalance, mission) : { lines: [], within: null };

  const checks: PilotCheck[] = mission
    ? buildReleasePack(mission, {
        picName: view.crewName ?? undefined,
        notams: brief?.items,
        notamAvailable: brief ? brief.status !== "needs-source" : undefined,
        notamNote: brief?.note,
        melItems: payload.melItems ? [...payload.melItems] : undefined,
      }).items.map((item) => ({
        id: item.id,
        label: item.label,
        state: item.state === "present" ? "done" : item.state,
        detail: item.summary,
      }))
    : [];

  const supportChecks: PilotCheck[] = support
    .filter((line) => line.serviceType !== "catering")
    .map((line) => ({
      id: line.id,
      label: line.serviceType,
      state: line.status === "confirmed" ? "done" : line.status === "cancelled" ? "missing" : "partial",
      detail: [line.airportIcao, line.vendorName, line.notes].filter((part) => part?.trim()).join(" · "),
    }));

  const logs = (payload.flightLogs ?? []).filter((row) => row.tripId && row.tripId === mission?.id);
  const log: PilotLogRow[] = logs.map((row, index) => {
    const amount = row.arrival?.fuelUsedAmount;
    const unit = row.arrival?.fuelUsedUnit?.trim();
    const fuel =
      typeof amount === "number" && Number.isFinite(amount)
        ? formatFuelFigure(
            convertFuel(amount, isFuelUnit(unit) ? unit : fuelUnit, fuelUnit, fuelKind),
            fuelUnit,
          )
        : "—";
    return {
      id: row.id || `log-${index}`,
      out: zulu(row.departedAtUtc),
      inn: zulu(row.arrivedAtUtc),
      fuel,
    };
  });

  const squawkIds = new Set(logs.flatMap((row) => row.arrival?.squawkIds ?? []));
  const squawks = (payload.squawks ?? [])
    .filter((row) => {
      if (row.status === "closed") return false;
      if (mission && (row.aircraftId === mission.aircraftId || row.registration === mission.registration)) return true;
      return squawkIds.has(row.id);
    })
    .map((row) => ({
      id: row.id,
      text: [row.severity, row.title, row.detail].filter((part) => part?.trim()).join(" · "),
    }));

  const messages: PilotNote[] = [];
  if (mission?.dispatchWarning?.trim()) messages.push({ id: "dispatch", text: mission.dispatchWarning.trim() });
  if (mission?.loadWarning?.trim()) messages.push({ id: "load", text: mission.loadWarning.trim() });
  if (trip?.cancelRemark?.trim()) messages.push({ id: "cancel", text: trip.cancelRemark.trim() });
  if (mission?.weatherNote?.trim()) messages.push({ id: "wx-note", text: mission.weatherNote.trim() });

  const tailId = mission?.aircraftId ?? "";
  const tailReg = mission?.registration || trip?.registration || "";
  const spec = (payload.fleet ?? []).find((row) => row.id === tailId || (tailReg && row.registration === tailReg));
  const hoppieTail = spec?.acarsCapable === "hoppie";
  const acarsUplinkIds: string[] = [];
  for (const row of mission?.acarsLog ?? []) {
    const packet = row.packet?.trim();
    if (!packet) continue;
    const id = row.id || `acars-${messages.length}`;
    const mirror = hoppieTail && row.dir === "up" && row.channel?.includes("hoppie") ? " Also sent to aircraft ACARS." : "";
    if (row.dir === "up" && row.id) acarsUplinkIds.push(row.id);
    messages.push({ id, text: `${packet}${mirror}` });
  }
  const previousNote = (payload.flightLogs ?? [])
    .filter((row) => row.tripId && row.tripId !== mission?.id && tailReg && row.registration === tailReg)
    .map((row) => row.arrival?.notes?.trim() || row.arrival?.maintenanceNotes?.trim() || "")
    .find(Boolean);
  const fob =
    spec && typeof spec.fuelOnBoardGal === "number" && Number.isFinite(spec.fuelOnBoardGal)
      ? `${spec.fuelOnBoardGal} gal on board`
      : "";
  const handover = fob || previousNote ? { fob, note: previousNote || "" } : null;
  const thisLog = logs[0];
  const simTimes: PilotSimTimes = {
    offBlock: thisLog?.offBlocksUtc?.trim() || thisLog?.departedAtUtc?.trim() || "",
    takeoff: thisLog?.wheelsOffUtc?.trim() || mission?.actualDepartureUtc?.trim() || "",
    landing: thisLog?.touchdownUtc?.trim() || "",
    onBlock: thisLog?.onBlocksUtc?.trim() || thisLog?.arrivedAtUtc?.trim() || "",
  };
  const logIds = new Set(logs.map((row) => row.id).filter((id): id is string => Boolean(id)));
  const logbook: PilotLogbookRow[] = (payload.logbook ?? [])
    .filter((row) => row.flightLogId && logIds.has(row.flightLogId))
    .map((row) => ({
      id: row.id,
      flightTimeMin: row.flightTimeMin,
      airTimeMin: row.airTimeMin ?? 0,
      landingsDay: row.landingsDay ?? 0,
      landingsNight: row.landingsNight ?? 0,
      registration: row.registration,
      aircraftType: row.aircraftType,
      route: row.route,
      role: row.role,
    }));
  const schedule: PilotScheduleRow[] = view.trips.map((row) => ({
    id: row.id,
    callsign: row.callsign,
    route: row.route,
    tail: row.registration,
    departureIso: row.departureIso,
    label: row.departureLabel,
  }));

  const crash = mission?.crashed
    ? {
        title: "Flight marked crashed",
        body: "Restart stays on the desk so hours and costs post once. This phone does not write them.",
      }
    : null;

  const paxCount = mission?.paxCount ?? trip?.paxCount ?? 0;
  const paxNames = (mission?.paxNames ?? []).map((name) => name.trim()).filter(Boolean);

  return {
    companyName: view.companyName,
    companyLogo: view.companyLogo,
    homeIcao: view.homeIcao,
    crewName: view.crewName ?? "",
    canDispatchWrite: view.canDispatchWrite,
    tripCount: view.trips.length,
    tripId: trip?.id ?? null,
    callsign: trip?.callsign || mission?.callsign || "",
    route: trip?.route || (mission ? `${mission.originIcao}–${mission.destIcao}` : ""),
    tail: mission?.registration || trip?.registration || "",
    aircraftName: mission?.aircraftName || trip?.aircraftName || "",
    std: trip?.departureLabel || (mission?.departureIso ? `STD ${zulu(mission.departureIso)}` : "STD —"),
    sta: trip?.arrivalLabel || "STA —",
    ete: trip?.eteLabel || "—",
    statusLabel: trip?.opsStatusLabel || trip?.lifecycleLabel || "",
    statusClass: trip?.statusPillClass || "",
    legs,
    fuel: mission ? fuelLines(mission, fuelUnit, fuelKind) : [],
    wb: wb.lines,
    wbWithin: wb.within,
    pax: mission || trip ? String(paxCount) : "",
    paxNames,
    catering,
    notams,
    notamNote: brief?.note?.trim() || "",
    originMetar: mission?.originMetar?.trim() || trip?.originMetar || "",
    destMetar: mission?.destMetar?.trim() || trip?.destMetar || "",
    ofp: ofpLines,
    ofpPdf: httpsPdf(ofp?.pdfUrl),
    duty: dutyView
      ? {
          statusLabel: dutyView.statusLabel,
          onDuty: dutyView.statusLabel === "ON DUTY" || dutyView.statusLabel === "PAUSED",
          level: dutyView.level,
          fdp: formatDutyHours(dutyView.fdpElapsedMin),
          fdpLimit: formatDutyHours(dutyView.limits.maxFdpMin),
          flight: formatDutyHours(dutyView.flightTimeTodayMin),
          flightLimit: formatDutyHours(dutyView.limits.maxFlightTimeMin),
          rest: dutyView.restElapsedMin == null ? "—" : formatDutyHours(dutyView.restElapsedMin),
          restLimit: formatDutyHours(dutyView.limits.minRestMin),
        }
      : null,
    handover,
    simTimes,
    logbook,
    schedule,
    checks: [...checks, ...supportChecks],
    log,
    squawks,
    messages,
    acarsHoppie: hoppieTail,
    acarsUplinkIds,
    crash,
    hero: heroFromPayload(payload, now, dutyOverride),
  };
}

/** Subsection copy for the pilot edit dock. Hidden sections are not requested. */
export function pilotEditLines(board: PilotBoard, tab: EditTab): string[] {
  if (tab === "checklist") return board.checks.map((row) => `${row.label}: ${row.state}${row.detail ? ` — ${row.detail}` : ""}`);
  if (tab === "flight" || tab === "trip") return board.legs.map((leg) => `${leg.seq}. ${leg.route} ${leg.out}${leg.fuel ? ` · ${leg.fuel}` : ""}`);
  if (tab === "pax") return [board.pax ? `${board.pax} pax` : "No passengers on this ticket", ...board.paxNames, ...board.catering];
  if (tab === "wb") return board.wb.length ? board.wb : ["W&B sheet is not on this board."];
  if (tab === "fuel") return board.fuel.length ? board.fuel : ["No fuel plan on this ticket."];
  if (tab === "wx") {
    const lines = [board.originMetar, board.destMetar].filter(Boolean);
    return lines.length ? lines : ["No METAR on this ticket."];
  }
  if (tab === "ofp") return board.ofp.length ? board.ofp : ["No SimBrief OFP on this ticket."];
  return [];
}

export type WeekDay = {
  key: string;
  label: string;
  hasTrip: boolean;
  isToday: boolean;
};

function dayKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Seven UTC days centred on today. A day is marked when a trip departs that day. */
export function weekAround(now: Date, departures: readonly string[]): WeekDay[] {
  const keys = new Set(
    departures
      .map((iso) => {
        const time = Date.parse(iso);
        return Number.isFinite(time) ? new Date(time).toISOString().slice(0, 10) : "";
      })
      .filter(Boolean),
  );
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 3));
  const today = dayKey(now);
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(start.getTime() + index * 86_400_000);
    const key = dayKey(date);
    return {
      key,
      label: date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", timeZone: "UTC" }),
      hasTrip: keys.has(key),
      isToday: key === today,
    };
  });
}
